import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopupsMutations } from '../../hooks/useTopupsMutations';
import type { Topup } from '../../models';

export interface ApproveTopupModalRef {
  open: (topup: Topup) => void;
  close: () => void;
}

interface ApproveTopupModalProps {
  onSuccess?: () => void;
}

export const ApproveTopupModal = forwardRef<ApproveTopupModalRef, ApproveTopupModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('topupsModule');
  const { toast } = useToast();
  const currency = useCurrency();
  const modalRef = useRef<ModalCenterRef>(null);
  const { approveTopup } = useTopupsMutations();

  const [topup, setTopup] = useState<Topup | null>(null);
  const [approvedAmount, setApprovedAmount] = useState('');

  useImperativeHandle(ref, () => ({
    open: (t) => {
      setTopup(t);
      setApprovedAmount(t.confirmed_amount?.toString() || t.declared_amount.toString());
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const handleConfirm = async () => {
    if (!topup) return;
    try {
      await approveTopup.mutateAsync({
        id: topup.id,
        payload: approvedAmount ? { approved_amount: Number(approvedAmount) } : undefined,
      });
      toast({
        title: t('modals.approve.success.title'),
        description: t('modals.approve.success.description'),
      });
      modalRef.current?.closeModal();
      onSuccess?.();
    } catch (error) {
      toast({
        title: t('errors.approveFailed'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = approveTopup.isPending;

  return (
    <ModalCenter ref={modalRef} title={t('modals.approve.title')} width="lg">
      <div className="space-y-4">
        <p className="text-sm text-foreground">{t('modals.approve.description')}</p>

        {topup && (
          <div className="bg-muted rounded-lg p-3 space-y-1">
            <p className="text-sm">
              <span className="font-medium">ID:</span> #{topup.id}
            </p>
            <p className="text-sm">
              <span className="font-medium">{t('modals.approve.fields.declared_amount')}:</span> {topup.declared_amount}{' '}
              {currency}
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('modals.approve.fields.approved_amount')} ({t('modals.approve.fields.optional')})
          </label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={approvedAmount}
            onChange={(e) => setApprovedAmount(e.target.value)}
            disabled={isLoading}
            placeholder={topup?.declared_amount.toString()}
          />
          <p className="text-xs text-muted-foreground">{t('modals.approve.fields.approved_amount_hint')}</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
            {t('buttons.cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('buttons.approving') : t('actions.approve')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ApproveTopupModal.displayName = 'ApproveTopupModal';
